import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getEquipmentById, getUserDiscount } from '../services/supabase';
import BookingWidget from '../components/booking/BookingWidget';
import { useEffect, useState } from 'react';
import { getCurrentUser } from '../services/telegram-auth';
import { useNavigate } from 'react-router-dom';

// Определение типов для статуса оборудования
type EquipmentStatus = 'available' | 'maintenance' | 'booked';

// Определение типов для объектов статуса
type StatusLabels = {
    [key in EquipmentStatus]: string;
};

type StatusClasses = {
    [key in EquipmentStatus]: string;
};

const EquipmentDetailPage = () => {
    const { id } = useParams<{ id: string }>();
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [userDiscount, setUserDiscount] = useState<number>(0);
    const [applyDiscount, setApplyDiscount] = useState<boolean>(true);
    const navigate = useNavigate();

    // Проверяем авторизацию пользователя при загрузке страницы
    useEffect(() => {
        const currentUser = getCurrentUser();
        setIsAuthenticated(!!currentUser);
        console.log('Страница детального просмотра - статус авторизации:', !!currentUser);
    }, []);

    // Получаем информацию о скидке пользователя
    useEffect(() => {
        const fetchUserDiscount = async () => {
            try {
                const { data, error } = await getUserDiscount();
                if (error) {
                    console.error('Ошибка при получении скидки:', error);
                } else if (data !== undefined) {
                    setUserDiscount(data);
                    console.log('Скидка пользователя:', data);
                }
            } catch (error) {
                console.error('Ошибка при получении скидки:', error);
            }
        };
        
        fetchUserDiscount();
    }, []);

    const { data: equipment, isLoading, error } = useQuery({
        queryKey: ['equipment', id],
        queryFn: async () => {
            if (!id) return null;
            const { data, error } = await getEquipmentById(id);
            if (error) throw error;
            return data;
        },
        enabled: !!id,
    });

    // Добавляем обработку скидки
    const discountedPrice = equipment && userDiscount > 0 && applyDiscount 
        ? Math.round(equipment.daily_rate * (100 - userDiscount) / 100) 
        : equipment?.daily_rate || 0;

    // Обработчик бронирования

    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-[50vh]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (error || !equipment) {
        return (
            <div className="text-center py-12 bg-red-50 rounded-lg">
                <h3 className="text-xl font-semibold mb-2 text-red-700">Ошибка загрузки</h3>
                <p className="text-red-500">
                    Не удалось загрузить информацию об оборудовании. Пожалуйста, попробуйте позже.
                </p>
            </div>
        );
    }

    const statusLabel: StatusLabels = {
        available: 'Доступно',
        maintenance: 'На обслуживании',
        booked: 'Забронировано',
    };

    const statusClass: StatusClasses = {
        available: 'bg-green-100 text-green-800',
        maintenance: 'bg-amber-100 text-amber-800',
        booked: 'bg-red-100 text-red-800',
    };

    // Получаем статус оборудования с проверкой на валидность
    const status: EquipmentStatus =
        (equipment.status as EquipmentStatus) || 'available';

    return (
        <div className="max-w-6xl mx-auto px-4">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-800">{equipment.name}</h1>
                <div className="flex items-center mt-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${statusClass[status]}`}>
                        {statusLabel[status]}
                    </span>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Левая часть - информация об оборудовании */}
                <div className="lg:col-span-2">
                    <div className="bg-white rounded-lg overflow-hidden shadow-sm border border-gray-200 mb-6">
                        {equipment.image_url ? (
                            <img
                                src={equipment.image_url}
                                alt={equipment.name}
                                className="w-full h-80 object-contain bg-gray-50 p-4"
                            />
                        ) : (
                            <div className="w-full h-80 flex items-center justify-center bg-gray-50">
                                <span className="text-gray-400 text-lg">Изображение отсутствует</span>
                            </div>
                        )}
                    </div>

                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h2 className="text-xl font-semibold mb-4">Описание</h2>
                        <p className="text-gray-700 mb-6">{equipment.description || 'Описание отсутствует'}</p>

                        <h2 className="text-xl font-semibold mb-4">Характеристики</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex justify-between p-3 bg-gray-50 rounded-md">
                                <span className="text-gray-500">Категория:</span>
                                <span className="font-medium">{equipment.category_id || 'Не указана'}</span>
                            </div>
                            <div className="flex justify-between p-3 bg-gray-50 rounded-md">
                                <span className="text-gray-500">Стоимость аренды:</span>
                                <div className="flex flex-col items-end">
                                    {userDiscount > 0 ? (
                                        <>
                                            <div className="flex items-center mb-1">
                                                <span className="text-xs text-gray-600 mr-2">Применить скидку {userDiscount}%:</span>
                                                <label className="relative inline-flex items-center cursor-pointer">
                                                    <input 
                                                        type="checkbox" 
                                                        checked={applyDiscount} 
                                                        onChange={() => setApplyDiscount(!applyDiscount)} 
                                                        className="sr-only peer"
                                                    />
                                                    <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-yellow-500"></div>
                                                </label>
                                            </div>
                                            {applyDiscount && (
                                                <>
                                                    <span className="font-medium">{discountedPrice} ₽/день</span>
                                                    <span className="text-xs text-gray-500 line-through">{equipment.daily_rate} ₽/день</span>
                                                    <span className="text-xs text-green-600">Ваша скидка: {userDiscount}%</span>
                                                </>
                                            )}
                                            {!applyDiscount && (
                                                <span className="font-medium">{equipment.daily_rate} ₽/день</span>
                                            )}
                                        </>
                                    ) : (
                                        <span className="font-medium">{equipment.daily_rate} ₽/день</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Правая часть - виджет бронирования */}
                <div className="lg:col-span-1">
                    {isAuthenticated ? (
                        <BookingWidget
                            dailyRate={equipment.daily_rate || 0}
                            equipmentId={equipment.id}
                            status={status}
                        />
                    ) : (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <h2 className="text-xl font-semibold mb-6">Бронирование</h2>
                            <div className="bg-blue-50 p-4 rounded-md mb-4 border border-blue-100">
                                <h3 className="text-blue-800 font-medium mb-2">Требуется авторизация</h3>
                                <p className="text-blue-700 mb-4 text-sm">
                                    Для бронирования оборудования необходимо войти в систему или зарегистрироваться.
                                </p>
                                <button 
                                    onClick={() => {
                                        localStorage.setItem('authRedirectUrl', window.location.pathname);
                                        navigate('/login');
                                    }}
                                    className="w-full bg-primary text-white py-2 px-4 rounded-md hover:bg-primary-600 transition-colors"
                                >
                                    Войти в систему
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EquipmentDetailPage; 